<template>
  <div>
    <popup-hint hint="invoices">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Invoices">
          <template v-slot:left v-if="salesStatus.draftInvoices">
            <list-notification v-model="showStatus">
              <v-list-item v-if="salesStatus.draftInvoices">
                <v-icon>mdi-arrow-right-thin</v-icon>
                {{salesStatus.draftInvoices}} draft invoice{{salesStatus.draftInvoices===1?'':'s'}}
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.from=null;filters.to=null;filters.customerId=null;filters.customerName=null;filters.search=null;filters.draft='1'})">View</v-btn>
              </v-list-item>
            </list-notification>
          </template>
          <template v-slot:right>
            <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left>
              <template v-slot:activator="{ on }">
                <v-icon small v-on="on">mdi-tune-variant</v-icon>
              </template>
              <v-card width="400">
                <sales-preferences/>
              </v-card>
            </v-menu>
          </template>
        </breadcrumbs>
      </template>
      <template v-slot:top>
        Invoices can be used to record a bill sent to a customer for a shipment.
      </template>
    </popup-hint>

    <purtrak-table name="invoices" objectType="invoices" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="invoices2" v-model="selected"
                   fields="id,draft,voided,date,due,term,number,customerId,customerName,customerLicense,privateNote,total,currency,exchangeRate,qbo,type,paymentMethod,paymentMethodId,exchangeRate,salesRepId,salesRepName,sent,email,paymentRefNum,discount{percent,amount,value},paid,attachmentURL"
                   :totals.sync="totals" totalFields="totalValue"
                   :listFilters="listFilters"
                   :item-class="invoice => (invoice.draft ? 'grey--text ' : '')"
                   @dblclick="invoice => $router.push({name:'SalesInvoicesEdit',params:{invoiceId:invoice.id}})"
                   @itemExpanded="({item, value}) => expandInvoice(item, value)"
                   title="Invoices"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[filters.from?dateDisplay:'',
                                           filters.draft=='1'?'Draft':'',
                                           filters.search?searchDisplay:'',
                                           filters.salesRepId!=null?'Rep: ' + salesRepName:'']}"
                   download-endpoint="invoicesExportCSV">
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.from=null;filters.to=null;filters.paymentMethodId='';filters.qbo='2';filters.draft='0',salesRepId=null;filters.search='';filters.customerId=null">
          <v-list-item>
            <filter-search :label="[{text:'Name',value:'customerName'},{text:'License #',value:'customerLicense'},{text:'Number',value:'number'},{text:'Payment',value:'paymentRefNum'}]" v-model="filters.search" :search-field.sync="filters.searchField" :search-display.sync="searchDisplay"/>
          </v-list-item>
          <v-list-item>
            <date-range :from.sync="filters.from" :to.sync="filters.to" v-model="dateDisplay" option="All" allow-all hide-details/>
          </v-list-item>
          <v-list-item dense>
            <v-checkbox v-model="filters.draft" true-value="1" false-value="0" label="Show draft only"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Paid status" dense outlined :items="[{value:'',text:'All'},{divider:true},{value:'1',text:'Paid'},{value:'0',text:'Not paid'}]" v-model="filters.paid"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Type" dense outlined :items="paymentMethods" item-value="id" item-text="name" v-model="filters.paymentMethodId"/>
          </v-list-item>
          <v-list-item v-if="connectedToAccounting">
            <v-autocomplete label="QuickBooks" dense outlined :items="[{value:'2',text:'All'},{divider:true},{value:'1',text:'Created'},{value:'0',text:'Not created'}]" v-model="filters.qbo"/>
          </v-list-item>
          <v-list-item v-if="salesReps && salesReps.length > 2 && !restrictedToSalesRep">
            <v-autocomplete label="Sales representative" dense outlined :items="salesReps" item-value="id" item-text="name" v-model="filters.salesRepId"/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search='';filters.customerId=null">{{searchDisplay}}</v-chip>
          <v-chip v-show="filters.from!=null" close small class="ml-1" @click:close="filters.from=null;filters.to=null">{{dateDisplay}}</v-chip>
          <v-chip v-show="filters.draft=='1'" close small class="ml-1" @click:close="filters.draft='0'">Draft only</v-chip>
          <v-chip v-show="filters.paid!=''" close small class="ml-1" @click:close="filters.paid=''">{{ {'1':'Paid','0':'Not paid'}[filters.paid] }}</v-chip>
          <v-chip v-show="filters.paymentMethodId!=''" close small class="ml-1" @click:close="filters.paymentMethodId=''">{{paymentMethodName}}</v-chip>
          <v-chip v-show="filters.qbo!='2'" close small class="ml-1" @click:close="filters.qbo='2'">{{filters.qbo=='1'?'Created':'Not created'}} in QBO</v-chip>
          <v-chip v-show="filters.salesOrderNumber" close small class="ml-1" @click:close="filters.salesOrderId=null;filters.salesOrderNumber=null">Sales order {{filters.salesOrderNumber}}</v-chip>
          <v-chip v-show="filters.salesRepId!=null" close small class="ml-1" @click:close="filters.salesRepId=null">{{salesRepName}}</v-chip>
          <v-chip v-show="filters.customerName" close small class="ml-1" @click:close="filters.customerId=null;filters.customerName=''">{{filters.customerName}}</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'SalesInvoicesEditNew'}"><v-list-item-title>Create invoice</v-list-item-title></v-list-item>
          <v-divider/>
        </template>
        <template v-if="company.allowRecurringTransactions">
          <v-list-item :to="{name:'RecurringTransactions',query:{targetType:'Invoice'}}"><v-list-item-title>Recurring invoices</v-list-item-title></v-list-item>
          <v-divider/>
        </template>
        <v-menu :nudge-left="220" v-if="!$vuetify.breakpoint.mobile">
          <template v-slot:activator="{on:reports}">
            <v-list-item v-on="reports">
              <v-list-item-title>Reports</v-list-item-title>
              <v-list-item-action><v-icon>mdi-chevron-right</v-icon></v-list-item-action>
            </v-list-item>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Invoices',to:{name:'SalesInvoices'}}]},query:{to:'reports/sales/ReportsSalesByItem'}}"><v-list-item-title>Sales summary by item</v-list-item-title></v-list-item>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Invoices',to:{name:'SalesInvoices'}}]},query:{to:'reports/sales/ReportsSalesByCustomer'}}"><v-list-item-title>Sales summary by customer</v-list-item-title></v-list-item>
            <v-divider/>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Invoices',to:{name:'SalesInvoices'}}]},query:{to:'reports/sales/ReportsSalesDetailsByItem'}}"><v-list-item-title>Sales detail by item</v-list-item-title></v-list-item>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Invoices',to:{name:'SalesInvoices'}}]},query:{to:'reports/sales/ReportsSalesDetailsByCustomer'}}"><v-list-item-title>Sales detail by customer</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <v-menu offset-y top>
          <template v-slot:activator="{ on }">
            <v-btn color="primary" outlined x-small style="position: absolute" class="mt-5 ml-10" v-on="on">Batch commands</v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="print()"><v-list-item-title>Print</v-list-item-title></v-list-item>
            <v-divider/>
            <v-list-item @click="payMultiple(true)"><v-list-item-title>Pay</v-list-item-title></v-list-item>
            <v-list-item @click="payMultiple(false)"><v-list-item-title>Unpay</v-list-item-title></v-list-item>
            <v-list-item @click="voidInvoice()"><v-list-item-title>Void</v-list-item-title></v-list-item>
            <v-list-item @click="deleteMultiple=true;showDelete=true"><v-list-item-title>Delete</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:item.date="{item,printing}">
        {{item.date | formatDate}}
        <div v-if="!printing && item.draft" class="caption black--text">Draft</div>
        <div v-else-if="!printing && item.voided" class="caption black--text">Voided</div>
      </template>
      <template v-slot:item.draft="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.voided="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.paid="{item,value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
        <div v-if="!printing && !value && !readOnly && !item.draft">
          <v-btn rounded color="warning lighten-1" x-small @click="pay(item,true)">PAY</v-btn>
        </div>
      </template>
      <template v-slot:item.due="{value}">
        {{value | formatDate}}
      </template>
      <template v-slot:item.qbo="{item,value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <template v-else-if="value">
          <div><v-icon>mdi-check</v-icon></div>
          <template v-if="connectedToAccounting && item.qbo && item.email">
            <v-btn v-if="!item.sent" rounded color="success" x-small @click="send(item)">Send</v-btn>
            <div v-else class="success--text">Sent</div>
          </template>
        </template>
      </template>
      <template v-slot:item.type="{value,item,printing}">
        {{value}}
        <div v-if="!printing && value !== 'Invoice'" class="text-caption">{{item.paymentMethod}}</div>
      </template>
      <template v-slot:item.customerName="{item,printing}">
        {{item.customerName}}
        <div v-if="!printing" class="text-caption">{{item.privateNote}}</div>
      </template>
      <template v-slot:item.total="{item,value,printing}">
        {{item.currency===homeCurrency?'':item.currency}} {{value | currency}}
        <div v-if="!printing && item.currency!==homeCurrency" class="text-caption">{{value*item.exchangeRate | currency}}</div>
      </template>
      <template v-slot:header.attachmentURL>
        <v-icon>mdi-paperclip</v-icon>
      </template>
      <template v-slot:item.attachmentURL="{value}">
        <v-icon v-if="value" class="green--text" @click="open(value)">mdi-paperclip</v-icon>
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'SalesInvoicesEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <a v-show="item.open" @click.stop="ship(item)" class="mr-2">Ship</a>
        <v-menu :nudge-width="150" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Invoice operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="print(item)"><v-list-item-title>Print</v-list-item-title></v-list-item>
            <template v-if="connectedToAccounting && item.qbo">
              <v-list-item @click="printQBO(item)"><v-list-item-title>View {{item.type==='Invoice'?'invoice':'receipt'}} from QBO</v-list-item-title></v-list-item>
              <v-list-item @click="send(item)"><v-list-item-title>Send invoice from QBO</v-list-item-title></v-list-item>
            </template>
            <template v-if="!readOnly && !item.draft">
              <v-list-item @click="pay(item, !item.paid)"><v-list-item-title>{{item.paid?'Unp':'P'}}ay</v-list-item-title></v-list-item>
            </template>
            <v-list-item v-if="company.allowAudit && company.permissions.includes('audit')" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
            <template v-if="!readOnly">
              <v-divider/>
              <v-list-item :to="{name:'SalesInvoicesEdit',params:{invoiceId:item.id}}"><v-list-item-title>Edit invoice</v-list-item-title></v-list-item>
              <v-list-item :to="{name:'SalesInvoicesEditNew',params:{copyInvoiceId:item.id}}"><v-list-item-title>Copy invoice</v-list-item-title></v-list-item>
              <v-list-item @click="updatePrivateNote(item)"><v-list-item-title>Update private note</v-list-item-title></v-list-item>
              <v-list-item v-if="!item.voided" @click="voidInvoice(item)"><v-list-item-title>Void invoice</v-list-item-title></v-list-item>
              <v-list-item @click="invoice=item;deleteMultiple=false;showDelete=true"><v-list-item-title>Delete invoice</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:body.append="{headers,printing}" v-if="!$vuetify.breakpoint.mobile">
        <tr :class="darkMode ? 'total-row-dark' : 'total-row'">
          <template v-for="header of headers">
            <td class="text-right" v-if="header.value==='total' && values">
              {{totals.totalValue | currency}}
            </td>
            <td v-else-if="!printing || header.print === undefined || header.print"/>
          </template>
        </tr>
      </template>
      <template v-slot:expanded-item="{ headers,item }">
        <td :colspan="headers.length">
          <v-card class="my-4" :class="expansionPanelClass">
          <v-simple-table dense class="pl-4 text-caption" :class="expansionPanelClass" style="width:70%">
            <thead>
              <tr>
                <th :class="expansionPanelClass" width="40%">Description</th>
                <th :class="expansionPanelClass" width="15%">SO #</th>
                <th class="text-right" :class="expansionPanelClass" width="15%">Quantity</th>
                <th class="text-right" :class="expansionPanelClass" width="15%">Unit price</th>
                <th class="text-right" :class="expansionPanelClass" width="15%">Amount</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="line of item.lines">
                <tr :key="line.key">
                  <td class="text-caption">{{line.description}}</td>
                  <td class="text-caption">
                    <a @click="$router.push({name:'SalesSalesOrdersEdit',params:{salesOrderId:line.salesOrderId}})">
                      {{line.salesOrderNumber}}
                    </a>
                  </td>
                  <td class="text-caption text-right">
                    {{line.quantity| number(line.precision)}} {{line.units}}
                  </td>
                  <template v-if="!line.items">
                    <td class="text-caption text-right">${{line.unitPrice | number(4)}}</td>
                    <td class="text-caption text-right">{{line.amount | currency}}</td>
                  </template>
                  <td v-else colspan="2"/>
                </tr>
                <tr v-for="bundlineLine of line.items" :key="bundlineLine.key">
                  <td class="text-caption"><span class="ml-4">{{bundlineLine.description}}</span></td>
                  <td class="text-caption">
                    <a @click="$router.push({name:'SalesSalesOrdersEdit',params:{salesOrderId:bundlineLine.salesOrderId}})">
                      {{bundlineLine.salesOrderNumber}}
                    </a>
                  </td>
                  <td class="text-caption text-right">{{line.quantity}}x{{bundlineLine.quantity | number(bundlineLine.precision)}} {{bundlineLine.units}}</td>
                  <td class="text-caption text-right">${{bundlineLine.unitPrice | number(4)}}</td>
                  <td class="text-caption text-right">{{bundlineLine.amount | currency}}</td>
                </tr>
              </template>
              <tr v-if="item.discount.amount || (item.taxLines && item.taxLines.length)">
                <td class="text-caption text-right" colspan="4">Subtotal</td>
                <td class="text-caption text-right">{{item.subTotal | currency}}</td>
              </tr>
              <tr v-if="item.discount.amount">
                <td class="text-caption text-right" colspan="4">Discount
                  <template v-if="item.discount.percent">{{item.discount.amount | number(2)}}%</template>
                  <template v-else>{{item.discount.amount | currency}}</template>
                </td>
                <td class="text-caption text-right">- {{item.discount.value | currency}}</td>
              </tr>
              <template v-for="tax of item.taxLines">
                <tr :key="tax.id">
                  <td class="text-caption text-right" colspan="4">
                    {{tax.name}} @ {{tax.percent}}% on {{tax.onAmount | currency}}
                  </td>
                  <td class="text-caption text-right">{{tax.amount | currency}}</td>
                </tr>
              </template>
              <tr>
                <td class="text-caption text-right" colspan="4">Total</td>
                <td class="text-caption text-right">{{item.total | currency}}</td>
              </tr>
            </tbody>
          </v-simple-table>
          </v-card>
        </td>
      </template>
    </purtrak-table>

    <component :is="dialog" v-bind="dialogProperties" :invoice="invoice" :invoices="selected" @done="dialog = null;dialogDone()" @refresh="$refs.table.buildList($event)"/>
    <delete-object v-model="showDelete" :name="deleteMultiple ? 'ALL selected invoices' : 'this invoice'" @delete="performDelete(invoice.id)"/>
  </div>
</template>

<script>
import performDelete from '@/mixins/performDelete'
import SalesPreferences from '@/views/sales/SalesPreferences'

export default {
  name: 'SalesInvoices',
  mixins: [performDelete],
  components: {SalesPreferences},
  data() {
    return {
      filters: {
        search: '',
        searchField: 'customerName',
        draft: '0',
        paid: '',
        from: null,
        to: null,
        future: '1',
        customerId: null,
        paymentMethodId: '',
        qbo: '2',
        salesOrderId: null,
        salesOrderNumber: null,
        salesRepId: null,
      },
      totals: {totalValue:0},
      salesStatus: {},
      showStatus: false,
      paymentMethods: [{id:'',name:'All'},{divider:true},{id:'invoice',name:'Invoice'}],
      salesReps: [{id:null,name:'All'},{divider:true}],
      dateDisplay: '',
      searchDisplay: '',
      invoice: {},
      showDelete: false,
      deleteMultiple: false,
			dialog: null,
      dialogProperties: {},
      voiding: false,
      defaultColumns: [{ value: 'date', visible:true},
                       { value: 'customerName', visible:true},
                       { value: 'customerLicense', visible:false},
                       { value: 'due', visible:true},
                       { value: 'term', visible:false},
                       { value: 'qbo', visible:true},
                       { value: 'type', visible:true},
                       { value: 'paymentMethod', visible:false},
                       { value: 'paymentRefNum', visible:false},
                       { value: 'number', visible:true},
                       { value: 'total', visible:true},
                       { value: 'paid', visible:false},
                       { value: 'attachmentURL', visible:true,excludeFromExport:true},
                       { value: 'draft', visible:false},
                       { value: 'voided', visible:false},
                       { value: 'salesRepName', visible:false},
                       { value: 'privateNote', visible:false}],
      headers: [{ text: 'DATE', value: 'date', width:'15%',sortable:true},
                { text: 'DUE', value: 'due', width:'15%',sortable:true},
                { text: 'TERMS', value: 'term', width:'15%',sortable:false},
                { text: 'QBO', value: 'qbo' ,width:'10%',align:'center',sortable:true, omit:() => !this.connectedToAccounting},
                { text: 'TYPE', value: 'type' ,width:'15%',sortable:true},
                { text: 'PAYMENT METHOD', value: 'paymentMethod' ,width:'15%',sortable:false},
                { text: 'PAYMENT REF', value:'paymentRefNum', width:'10%', sortable:false},
                { text: 'NUMBER', value: 'number' ,width:'10%',sortable:true},
                { text: 'CUSTOMER', value: 'customerName' ,width:'30%',sortable:true},
                { text: 'LICENSE #', value: 'customerLicense' ,width:'10%',sortable:true},
                { text: 'NOTE', value: 'privateNote' ,width:'25%',sortable:true},
                { text: 'TOTAL', value: 'total' ,width:'15%',sortable:true,align:'right'},
                { text: 'SALES REP', value: 'salesRepName' ,width:'20%',sortable:true},
                { text: 'VOID?', value: 'voided' ,width:'10%',sortable:true,align:'center'},
                { text: 'PAID?', value: 'paid' ,width:'10%',sortable:false,align:'center'},
                { text: 'ATTACHMENT', value: 'attachmentURL' ,width:'5%',sortable:false,print:false},
                { text: 'DRAFT?', value: 'draft' ,width:'10%',sortable:true,align:'center'}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    readOnly() {
      return !this.company.permissions.includes('selling.write');
    },
    homeCurrency() {
      return this.company.settings.homeCurrency;
    },
    connectedToAccounting() {
      return this.company.connectedToAccounting;
    },
    paymentMethodName() {
      return this.paymentMethods.find(p => p.id === this.filters.paymentMethodId)?.name;
    },
    listFilters() {
      const filters = [];
      if (this.filters.from)
        filters.push({field:'date', comparator:'ge', value:`${this.filters.from} 00:00:00`});
      if (this.filters.to)
        filters.push({field:'date', comparator:'le', value:`${this.filters.to} 23:59:59`});
      if (this.filters.salesRepId)
        filters.push({field:'salesRepId', comparator:'eq', value:this.filters.salesRepId});
      if (this.filters.customerId !== null)
        filters.push({field:'customerId', comparator:'eq', value:this.filters.customerId});
      if (this.filters.search)
        filters.push({field:this.filters.searchField, comparator:'contains', value:this.filters.search});
      if (this.filters.salesOrderId)
        filters.push({field:'salesOrderId', comparator:'eq', value:this.filters.salesOrderId});

      if (this.filters.draft == '1')
        filters.push({field:'draft', comparator:'eq', value:'true'});
      if (this.filters.paid !== '')
        filters.push({field:'paid', comparator:'eq', value:this.filters.paid === '1' ? 'true' : 'false'});
      if (this.filters.qbo == '0')
        filters.push({field:'qbo', comparator:'eq', value:'false'});
      if (this.filters.qbo == '1')
        filters.push({field:'qbo', comparator:'ne', value:'true'});
      if (this.filters.paymentMethodId != '') {
        const paymentMethodId = this.filters.paymentMethodId === 'invoice' ? '' : this.filters.paymentMethodId;
        filters.push({field:'paymentMethodId', comparator:'eq', value:paymentMethodId});
      }
      return filters;
    },
    salesRepName() {
      return this.salesReps?.find(salesRep => salesRep.id === this.filters.salesRepId)?.name ?? '';
    },
    restrictedToSalesRep() {
      return this.salesReps.find(rep => rep.userId === this.$store.state.user.sub && rep.restricted);
    },
    values() {
      return this.$store.getters.company.permissions.includes('values');
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');
    this.$lazyListLoad(this.salesReps, 'salesReps2', 'id,name,restricted,userId', 100, [{field:'active', comparator:'eq', value:'true'}]);
    this.$lazyListLoad(this.paymentMethods, 'paymentMethods2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]);

    await this.getStatus();
    if (!['SalesInvoicesEdit', 'SalesInvoicesEditNew'].includes(this._previousRoute) &&
        Object.keys(this.$route.query).length === 0 &&
        this.salesStatus.draftInvoices && !localStorage.getItem('hideNotification')) {
      this.$nextTick(() => this.showStatus = true);
      setTimeout(() => this.showStatus = false, 10_000);  // Close in 10 seconds
    }
  },
  methods: {
    print(invoice) {
      this.invoice = invoice;
      this.dialog = () => import(`@/views/sales/invoices/SalesInvoicesPrint`);
    },
    voidInvoice(invoice) {
      this.invoice = invoice;
      this.voiding = true;
      this.dialog = () => import(`@/views/sales/invoices/SalesInvoicesVoid`);
    },
    dialogDone() {
      if (this.selected.length && this.voiding)
        this.$refs.table?.buildList();
      this.voiding = false;
    },
    async send(invoice) {
      if (!invoice.email) {
        this.$store.commit('setError', {type:'warning',message:`Cannot send this invoice since is doesn't have an email address`});
        return;
      }
      this.loading = true;
      const results = await this.$apiCall(`mutation SalesInvoices($id: ID!) {
                                              invoiceSend(id:$id) {
                                                sent
                                              }
                                            }`,
                                          {id:invoice.id});
      this.loading = false;
      if (!results.error) {
        invoice.sent = results.data.invoiceSend.sent;
        this.$store.commit('setError', {type:'success',message:`Invoice sent from QuickBooks Online`});
      }
    },
    async printQBO(invoice) {
      this.loading = true;
      const results = await this.$apiCall(`query SalesInvoices($id: ID!) {
                                                invoicePrint(id:$id)
                                              }`,
                                          {id:invoice.id});
      this.loading = false;
      if (!results.error) {
        const win = window.open();
        if (!win) {
          this.$store.commit('setError', {type:'warning',message:`Your browser permissions must allow pop-up windows`});
          return;
        }
        const document = win.document;
        const obj = document.createElement('object');
        obj.style.width="100%";
        obj.style.height="100%";
        obj.type = 'application/pdf';
        obj.data = 'data:application/pdf;base64,' + results.data.invoicePrint;
        document.body.appendChild(obj);
        win.document.close();
        win.focus();
      }
    },
    async expandInvoice(item, value) {
      if (value && !item.lines) {
        this.loading = true;
        const results = await this.$apiCall(`query SalesInvoices($id:ID!) {
                                                invoice(id:$id) {
                                                  subTotal
                                                  lines {
                                                    description,quantity,unitPrice,amount,precision,units,salesOrderNumber,salesOrderId
                                                    items {description,quantity,units,precision,unitPrice,amount,id,salesOrderNumber,salesOrderId}
                                                  }
                                                  taxLines {id,name,amount,onAmount,percent}
                                                }
                                              }`,
                                            {id:item.id});
        if (!results.error) {
          item.subTotal = results.data.invoice.subTotal;
          this.$set(item, 'lines', results.data.invoice.lines);
          this.$set(item, 'taxLines', results.data.invoice.taxLines);
          let key = 1;
          item.lines.forEach(line => line.key = key++);
        }
        this.loading = false;
      }
    },
    async getStatus() {
      this.loading = true;
      const results = await this.$apiCall(`query SalesInvoices {
                                                salesStatus {
                                                  draftInvoices
                                                }
                                              }`);
      if (!results.error)
        this.salesStatus = results.data.salesStatus;
      this.loading = false;
    },
    async pay(invoice, paid) {
      this.loading = true;
      const results = await this.$apiCall(`mutation SalesInvoices($invoiceId:ID!, $paid:Boolean!) {
                                            invoicePay(invoiceId: $invoiceId, paid: $paid) {
                                              paid
                                            }
                                           }`, {invoiceId:invoice.id, paid})
      this.loading = false;
      if (!results.error)
        invoice.paid = results.data.invoicePay.paid;
    },
    async payMultiple(paid) {
      this.loading = true;
      let success = 0;
      for (const invoice of this.selected) {
        const results = await this.$apiCall(`mutation SalesInvoices($invoiceId:ID!, $paid:Boolean!) {
                                              invoicePay(invoiceId: $invoiceId, paid: $paid) {
                                                paid
                                              }
                                            }`, {invoiceId:invoice.id, paid}, true)
        if (!results.error) {
          invoice.paid = results.data.invoicePay.paid;
          success ++;
        }
      }
      this.loading = false;
      this.$store.commit('setError', {type:'success',message:`${paid?'Paid':'Unpaid'} ${success} of ${this.selected.length} invoices`});
    },
    updatePrivateNote(invoice) {
      this.dialogProperties = {item:invoice, operation:'invoiceUpdatePrivateNote'};
      this.dialog = () => import(`@/components/UpdatePrivateNote`);
    },
    open(url) {
      const win = window.open(url);
      if (!win) {
        this.$store.commit('setError', {type:'warning',message:`Your browser permissions must allow pop-up windows`});
        return;
      }
    }
  }
}
</script>